import type { SVGProps } from 'react';

/**
 * Iconos de trazo, dibujados sobre una rejilla de 24 px.
 *
 * El CRM anterior usaba emoji. Se ven distintos en cada sistema operativo, no
 * heredan el color del texto y en impresión salen como recuadros. Estos son
 * SVG: escalan, toman `currentColor` y se ven igual en todas partes.
 */

type Props = SVGProps<SVGSVGElement> & { size?: number };

function Base({ size = 16, children, ...resto }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...resto}
    >
      {children}
    </svg>
  );
}

export const IconoBuscar = (p: Props) => (
  <Base {...p}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></Base>
);

export const IconoCampana = (p: Props) => (
  <Base {...p}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></Base>
);

export const IconoSol = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </Base>
);

export const IconoLuna = (p: Props) => (
  <Base {...p}><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" /></Base>
);

export const IconoMas = (p: Props) => (
  <Base strokeWidth={2.5} {...p}><path d="M12 5v14M5 12h14" /></Base>
);

export const IconoDescargar = (p: Props) => (
  <Base {...p}><path d="M12 3v12M7 10l5 5 5-5M5 21h14" /></Base>
);

export const IconoAlerta = (p: Props) => (
  <Base {...p}><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></Base>
);

export const IconoCerebro = (p: Props) => (
  <Base strokeWidth={1.8} {...p}>
    <path d="M12 3a4 4 0 0 0-4 4v1a3 3 0 0 0 0 6v1a4 4 0 0 0 8 0v-1a3 3 0 0 0 0-6V7a4 4 0 0 0-4-4Z" />
    <path d="M12 3v18M8.5 8h7M8.5 15h7" />
  </Base>
);

export const IconoCheck = (p: Props) => (
  <Base strokeWidth={2.5} {...p}><path d="M20 6 9 17l-5-5" /></Base>
);

export const IconoCerrar = (p: Props) => (
  <Base {...p}><path d="M18 6 6 18M6 6l12 12" /></Base>
);

export const IconoIzquierda = (p: Props) => (
  <Base {...p}><path d="m15 18-6-6 6-6" /></Base>
);

export const IconoDerecha = (p: Props) => (
  <Base {...p}><path d="m9 18 6-6-6-6" /></Base>
);

export const IconoSalir = (p: Props) => (
  <Base {...p}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></Base>
);

export const IconoRayo = (p: Props) => (
  <Base strokeWidth={2.2} {...p}><path d="M13 2 4.5 12.5H11l-1 9.5 8.5-10.5H12l1-9.5Z" /></Base>
);

export const IconoDocumento = (p: Props) => (
  <Base {...p}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
    <path d="M14 2v6h6M9 13h6M9 17h4" />
  </Base>
);

export const IconoPausa = (p: Props) => (
  <Base {...p}>
    <rect x="6" y="4" width="4" height="16" rx="1" />
    <rect x="14" y="4" width="4" height="16" rx="1" />
  </Base>
);

export const IconoPlay = (p: Props) => (
  <Base {...p}>
    <polygon points="5 3 19 12 5 21 5 3" />
  </Base>
);

export const IconoTabla = (p: Props) => (
  <Base {...p}>
    <rect width="18" height="18" x="3" y="3" rx="2" />
    <path d="M3 9h18M3 15h18M9 9v12" />
  </Base>
);

export const IconoCuadricula = (p: Props) => (
  <Base {...p}>
    <rect width="7" height="7" x="3" y="3" rx="1" />
    <rect width="7" height="7" x="14" y="3" rx="1" />
    <rect width="7" height="7" x="14" y="14" rx="1" />
    <rect width="7" height="7" x="3" y="14" rx="1" />
  </Base>
);

export const IconoFiltro = (p: Props) => (
  <Base {...p}>
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </Base>
);

export const IconoEscudoCheck = (p: Props) => (
  <Base {...p}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
    <path d="m9 12 2 2 4-4" />
  </Base>
);

export const IconoEmpresa = (p: Props) => (
  <Base {...p}>
    <path d="M3 21h18M5 21V7l8-4v18M13 7l6 4v10M9 9h1M9 13h1M9 17h1" />
  </Base>
);

export const IconoCalculadora = (p: Props) => (
  <Base {...p}>
    <rect width="16" height="20" x="4" y="2" rx="2" />
    <line x1="8" x2="16" y1="6" y2="6" />
    <path d="M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01" />
  </Base>
);

export const IconoEtiqueta = (p: Props) => (
  <Base {...p}>
    <path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z" />
    <path d="M7 7h.01" />
  </Base>
);

export const IconoMotorIA = (p: Props) => (
  <Base {...p}>
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </Base>
);

export const IconoUsuarios = (p: Props) => (
  <Base {...p}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Base>
);

export const IconoHistorial = (p: Props) => (
  <Base {...p}>
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l4 2" />
  </Base>
);

export const IconoCopiar = (p: Props) => (
  <Base {...p}>
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </Base>
);

export const IconoRestablecer = (p: Props) => (
  <Base {...p}>
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </Base>
);

export const IconoCandado = (p: Props) => (
  <Base {...p}>
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </Base>
);

export const IconoEditar = (p: Props) => (
  <Base {...p}>
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    <path d="m15 5 4 4" />
  </Base>
);

export const IconoBasura = (p: Props) => (
  <Base {...p}>
    <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    <line x1="10" x2="10" y1="11" y2="17" />
    <line x1="14" x2="14" y1="11" y2="17" />
  </Base>
);

export const IconoExterno = (p: Props) => (
  <Base {...p}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </Base>
);

export const IconoWhatsapp = (p: Props) => (
  <Base {...p}>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </Base>
);

export const IconoCorreo = (p: Props) => (
  <Base {...p}>
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </Base>
);

export const IconoCalendario = (p: Props) => (
  <Base {...p}>
    <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
    <line x1="16" x2="16" y1="2" y2="6" />
    <line x1="8" x2="8" y1="2" y2="6" />
    <line x1="3" x2="21" y1="10" y2="10" />
  </Base>
);

export const IconoCapas = (p: Props) => (
  <Base {...p}>
    <polygon points="12 2 2 7 12 12 22 7 12 2" />
    <polyline points="2 17 12 22 22 17" />
    <polyline points="2 12 12 17 22 12" />
  </Base>
);

export const IconoReloj = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </Base>
);

export const IconoImprimir = (p: Props) => (
  <Base {...p}>
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect width="12" height="8" x="6" y="14" />
  </Base>
);



